import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, User, X, Star } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ProfileOrders } from './ProfileOrders';
import { OSAvatarIndicator } from './OSAvatarIndicator';
import { useCliente } from '@/hooks/useClientes';
import { useClientesOSIndicador } from '@/hooks/useOrdensServico';

interface ClienteProfileProps {
  clienteId?: string;
  onClose?: () => void;
}

export function ClienteProfile({ clienteId, onClose }: ClienteProfileProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: cliente, isLoading } = useCliente(clienteId);
  const { data: osIndicadores } = useClientesOSIndicador(clienteId ? [clienteId] : undefined);

  if (!clienteId) {
    return (
      <div className="painel__convo-empty">
        <div className="painel__profile-contact-icn" style={{ width: 56, height: 56 }}>
          <User size={24} />
        </div>
        <p className="text-sm text-muted-foreground">
          {t('panel.selectClientDetails')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <>
        <div className="painel__profile-hdr-bar">
          <div className="painel__profile-hdr-title">{t('panel.profile', 'Perfil')}</div>
        </div>
        <div className="painel__profile-body">
          <Skeleton className="h-24 w-24 rounded-full" />
          <Skeleton className="h-5 w-32 mt-2" />
          <Skeleton className="h-4 w-20" />
        </div>
      </>
    );
  }

  const inicial = cliente?.nome?.charAt(0)?.toUpperCase() || '?';
  const codigo = cliente?.codigo_cliente || cliente?.telefone?.replace(/@[cs]\.us$/, '');
  const telefone = cliente?.telefone?.replace(/@[cs]\.us$/, '');
  const endereco = [cliente?.endereco_rua, cliente?.endereco_numero, cliente?.endereco_cidade]
    .filter(Boolean)
    .join(', ');

  const abrirPerfil = () => navigate(`/cliente-potential/${clienteId}`);

  const clienteParaOS = cliente
    ? {
        id: cliente.id,
        nome: cliente.nome,
        telefone: cliente.telefone,
        endereco_rua: cliente.endereco_rua,
        endereco_numero: cliente.endereco_numero,
        endereco_complemento: cliente.endereco_complemento,
        endereco_codigo_postal: cliente.endereco_codigo_postal,
        endereco_cidade: cliente.endereco_cidade,
        endereco_pais: cliente.endereco_pais,
      }
    : undefined;

  return (
    <>
      <div className="painel__profile-hdr-bar">
        <div className="painel__profile-hdr-title">{t('panel.profile', 'Perfil')}</div>
        <button
          type="button"
          className="painel__icon-btn"
          aria-label={t('common.close', 'Fechar')}
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>

      <div className="painel__profile-scroll">
        <div className="painel__profile-body">
          <button
            type="button"
            className="painel__profile-idbtn"
            onClick={abrirPerfil}
            aria-label={t('panel.viewProfile', 'Ver perfil')}
          >
            <div className="painel__profile-avt-wrap">
              <div className="painel__profile-avt">
                {cliente?.avatar_url ? <img src={cliente.avatar_url} alt={cliente.nome || ''} /> : inicial}
              </div>
              <OSAvatarIndicator
                osIndicador={clienteId ? osIndicadores?.[clienteId] ?? null : null}
                ledClassName="painel__profile-led"
              />
            </div>
            <div className="painel__profile-name">{cliente?.nome || t('common.noName')}</div>
          </button>
          {codigo && <div className="painel__profile-code">{codigo}</div>}
          {/* Rating placeholder — wire to avaliacoes_servico when surfaced */}
          <div className="painel__profile-rating">
            <Star size={14} fill="currentColor" style={{ color: 'hsl(42 95% 55%)' }} />
            4.7
            <span className="painel__profile-rating-lbl">
              ({t('panel.reviews', 'Avaliações')})
            </span>
          </div>
        </div>

        {(telefone || cliente?.email || endereco) && (
          <div className="painel__profile-contacts">
            {telefone && (
              <a href={`tel:${telefone}`} className="painel__profile-contact">
                <span className="painel__profile-contact-icn">
                  <Phone size={14} />
                </span>
                <span>{telefone}</span>
              </a>
            )}
            {cliente?.email && (
              <a href={`mailto:${cliente.email}`} className="painel__profile-contact">
                <span className="painel__profile-contact-icn">
                  <Mail size={14} />
                </span>
                <span className="truncate">{cliente.email}</span>
              </a>
            )}
            {endereco && (
              <div className="painel__profile-contact">
                <span className="painel__profile-contact-icn">
                  <MapPin size={14} />
                </span>
                <span className="line-clamp-2">{endereco}</span>
              </div>
            )}
          </div>
        )}

        <ProfileOrders clienteId={clienteId} clientePreSelecionado={clienteParaOS} />
      </div>
    </>
  );
}
